import { action, makeObservable, observable, override, runInAction } from 'mobx';

import { getCount, getPage } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';
import LicenseAuditStore from 'stores/licenseAudit/LicenseAuditStore';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from '../BaseListStore';
import { SortDirection } from 'constants/SortDirection';

const SEARCH_FIELDS = ['type', 'action', 'reason', 'search'];
const DEFAULT_FILTER = {
    search: ''
};

let filterUpdated = true;

export default class LicenseAuditListStore extends BaseListStore<LicenseAuditStore> {
    filteredCount = 0;

    constructor() {
        super({
            itemStore: LicenseAuditStore,
            sortBy: 'createdOn',
            sortDirection: SortDirection.DESC,
            defaultFilter: DEFAULT_FILTER
        });

        makeObservable(this, {
            resetFilter: override,
            clearIds: override,
            getTotalCount: action,
            getAuditEntries: action,
            filteredCount: observable
        });
    }

    get auditEntries(): LicenseAuditStore[] {
        return this.ids.map((id) => this.data[id]);
    }

    resetFilter(): void {
        this.filter = { ...DEFAULT_FILTER };
    }

    setFilter(filterItems: any) {
        this.filter = {
            ...this.filter,
            ...filterItems
        };
    }

    clearIds(): void {
        this.ids = [];
    }

    async getTotalCount(): Promise<void> {
        const totalCount = await getCount('LicenseAllocationTrackingHistory', { searchFields: SEARCH_FIELDS });
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.totalCount = totalCount;
        });
    }

    async getAuditEntries(args = {}, filterUpdate = false, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        if (!filterUpdated) filterUpdated = filterUpdate;

        const shouldReset = this._updateListState(args);

        const where = getConditions(this.filter);
        const searchFields = SEARCH_FIELDS;

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields,
                where,
                order: this.sort,
                include: [
                    {
                        relation: 'agent'
                    }
                ]
            }
        };

        const { data } = await getPage('LicenseAllocationTrackingHistory', options);
        const totalCount = await getCount('licenseAllocationTrackingHistory', { where, searchFields });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });

        if (filterUpdated) {
            runInAction(() => {
                this.filteredCount = totalCount;
                filterUpdated = false;
            });
        }
    }
}
